import React, { useMemo } from 'react';
import clsx from 'clsx';
import makeStyles from '@mui/styles/makeStyles';
import { DeleteOutline } from '@mui/icons-material';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, IconButton, Typography } from '@mui/material';
import { FACT_NAMES_STRING } from '@trnsact/business-criteria';
import { allFacts } from '../../../../constants';
import { FinanceProgramForm } from '../../../../schema';
import { SelectOption } from '../../../../../../global';
import { ConditionsFieldsByRuleType } from './ConditionsFieldsByRuleType';
import { SelectField, SwitchField } from '../../../../../../components/form';

interface Props {
  index: number;
  onRemove: VoidFunction;
  userType?: 'lender' | 'dealer';
  dealerOptions?: SelectOption[];
}

export const ChooseCriteriaRuleField = ({ index, onRemove, userType, dealerOptions = [] }: Props) => {
  const classes = useStyles();

  const { control, getValues } = useFormContext<FinanceProgramForm>();
  const { program } = useWatch<FinanceProgramForm>({ control });

  const rulesOptionsFilteredBySelected = useMemo(() => {
    const addedCriteriaTypes = getValues('program.first.criteria.conditions').map(({ fact }) => fact);

    // Filter out DEALERS fact for non-lender users
    const availableFacts =
      userType === 'lender' ? allFacts : allFacts.filter(fact => fact.value !== FACT_NAMES_STRING.DEALERS);

    const currentValue = availableFacts.find(fact => fact.value === addedCriteriaTypes[index]);

    return [
      currentValue,
      ...availableFacts.filter(({ value }) => !addedCriteriaTypes.includes(value)),
    ] as SelectOption[];
  }, [program, userType]);

  return (
    <Box className="arrayField">
      <Box className="arrayFieldHeader">
        <IconButton className="customIconBtn" size="small" onClick={onRemove}>
          <DeleteOutline color="error" />
        </IconButton>

        <Typography component="span" variant="subtitle2">
          Condition #{index + 1}
        </Typography>
      </Box>

      <Box className={clsx('formRow', classes.selectWrapper)}>
        <SelectField
          label="Rule"
          control={control}
          options={rulesOptionsFilteredBySelected}
          name={`program.first.criteria.conditions.${index}.fact`}
        />

        <ConditionsFieldsByRuleType index={index} dealerOptions={dealerOptions} />
      </Box>

      <Box>
        <SwitchField
          control={control}
          label="Required to Quote?"
          name={`program.first.criteria.conditions.${index}.isRequiredToQuote`}
          tooltip="Requires this condition to be met in order to generate a proposal"
        />
      </Box>
    </Box>
  );
};

const useStyles = makeStyles({
  selectWrapper: {
    '& > div': {
      flex: 1,
    },
  },
});
